import { Injectable, inject } from '@angular/core';
import { I18NService } from '@core';
import { ALAIN_I18N_TOKEN } from '@delon/theme';
import { Observable, Subject } from 'rxjs';

import { FacebookUser, FacebookPage, InstagramPage } from '../../types/facebook';

const GRAPH_API_VERSION = 'v25.0';

@Injectable({
  providedIn: 'root'
})
export class FacebookService {
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private sdkLoaded = false;
  private sdkLoadingPromise: Promise<void> | null = null;

  constructor() {}

  /**
   * 加載 Facebook SDK
   */
  loadFacebookSDK(appId: string): Promise<void> {
    if (this.sdkLoaded) {
      return Promise.resolve();
    }

    if (this.sdkLoadingPromise) {
      return this.sdkLoadingPromise;
    }

    this.sdkLoadingPromise = new Promise<void>((resolve, reject) => {
      if (typeof window.FB !== 'undefined') {
        this.sdkLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://connect.facebook.net/zh_TW/sdk.js';
      script.onload = () => {
        window.FB.init({
          appId: appId,
          cookie: true,
          xfbml: true,
          version: GRAPH_API_VERSION
        });
        this.sdkLoaded = true;
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Failed to load Facebook SDK'));
      };
      document.head.appendChild(script);
    });

    return this.sdkLoadingPromise;
  }

  /**
   * 獲取 Facebook 用戶信息
   */
  fetchUserInfo(accessToken: string): Promise<FacebookUser> {
    return new Promise((resolve, reject) => {
      if (!window.FB) {
        reject(new Error('Facebook SDK not loaded'));
        return;
      }

      window.FB.api(
        '/me',
        {
          fields: 'id,name,email,picture',
          access_token: accessToken
        },
        (response: any) => {
          if (response && !response.error) {
            const user: FacebookUser = {
              id: response.id,
              name: response.name,
              email: response.email ?? '',
              picture: response.picture?.data?.url ?? ''
            };
            resolve(user);
          } else {
            reject(new Error(response.error?.message ?? 'Failed to get Facebook user info'));
          }
        }
      );
    });
  }

  /**
   * 獲取 Facebook 頁面列表
   */
  getFacebookPages(accessToken: string): Promise<FacebookPage[]> {
    return new Promise((resolve, reject) => {
      if (!window.FB) {
        reject(new Error('Facebook SDK not loaded'));
        return;
      }

      window.FB.api(
        '/me/accounts',
        {
          fields: 'id,name,access_token,picture{url}',
          access_token: accessToken
        },
        (response: any) => {
          if (response && !response.error && response.data) {
            const pages: FacebookPage[] = response.data.map((page: any) => ({
              id: page.id,
              name: page.name,
              access_token: page.access_token,
              category: page.category,
              picture: page.picture,
              fan_count: page.fan_count,
              about: page.about,
              website: page.website,
              phone: page.phone,
              emails: page.emails,
              location: page.location
            }));
            resolve(pages);
          } else {
            reject(new Error(response.error?.message ?? 'Failed to get Facebook pages'));
          }
        }
      );
    });
  }

  /**
   * 獲取 Instagram 頁面列表
   */
  getInstagramPages(accessToken: string): Promise<InstagramPage[]> {
    return new Promise((resolve, reject) => {
      if (!window.FB) {
        reject(new Error('Facebook SDK not loaded'));
        return;
      }

      const fields =
        'category,description,emails,picture,name,access_token,instagram_business_account{id,name,username,profile_picture_url}';

      window.FB.api('/me/accounts', { fields, access_token: accessToken }, (response: any) => {
        if (response && !response.error) {
          // 過濾出有 Instagram 帳號的數據
          const instagramPages: InstagramPage[] = response.data
            .filter((page: any) => page.instagram_business_account)
            .map((page: any) => ({
              fansPageId: page.id,
              fansPageName: page.name,
              fansPage_access_token: page.access_token,
              id: page.instagram_business_account.id,
              name: page.instagram_business_account.name,
              username: page.instagram_business_account.username,
              profile_picture_url: page.instagram_business_account.profile_picture_url
            }));
          resolve(instagramPages);
        } else {
          reject(new Error(response?.error?.message ?? this.i18n.fanyi('facebook.get-instagram-accounts-failed')));
        }
      });
    });
  }

  /**
   * 生成 Facebook OAuth URL
   */
  generateOAuthUrl(clientId: string, redirectUri: string, scope: string[], state: string): string {
    const scopeString = scope.join(',');
    return (
      `https://www.facebook.com/${GRAPH_API_VERSION}/dialog/oauth?` +
      `client_id=${clientId}&` +
      `redirect_uri=${encodeURIComponent(redirectUri)}&` +
      'response_type=token&' +
      `scope=${encodeURIComponent(scopeString)}&` +
      `state=${state}&` +
      'auth_type=rerequest&' +
      'display=popup'
    );
  }

  /**
   * 打開 Facebook 登錄彈窗
   */
  openLoginPopup(oauthUrl: string): Window | null {
    const width = 840;
    const height = 430;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;

    // 保存当前窗口的源到 sessionStorage
    sessionStorage.setItem('parent_origin', window.location.origin);

    return window.open(oauthUrl, 'facebook_login', `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`);
  }
}
